import { ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { format } from 'date-fns';
import useHttp from '@/utils/httpClient';
import { useAppConfigStore } from '@stores/appConfig.store';
import { useBookingQueryStore } from '@stores/bookingQuery.store';
import { useOffersStore } from '@stores/offers.store';

const appConfigStore = useAppConfigStore();
const { applicationSetup } = storeToRefs(appConfigStore);
const bookingQueryStore = useBookingQueryStore();
const { query } = storeToRefs(bookingQueryStore);
const addonsCache = ref({});

const http = useHttp();

const isDirty = ref(true);

export const useAddonsStore = defineStore('addonsStore', () => {
  const addons = ref([]);

  const addAddon = (addon) => {
    const index = addons.value.findIndex((a) => a.id === addon.id);
    if (index !== -1) {
      addons.value[index] = addon;
    } else {
      addons.value.push(addon);
    }
  };

  const removeAddon = (addonId) => {
    addons.value = addons.value.filter((addon) => addon.id !== addonId);
  };

  const getAllAddons = async (roomId = 0) => {
    let occupancy = query.value.occupancy[0];
    if (roomId !== 0) {
      const offersStore = useOffersStore();
      const { currentRoom } = storeToRefs(offersStore);
      occupancy = query.value.occupancy[currentRoom.value];
    }

    const params = {
      language: applicationSetup.value.language,
      currency: applicationSetup.value.currency,
      checkin: format(query.value.startDate, 'yyyy-MM-dd'),
      checkout: format(query.value.endDate, 'yyyy-MM-dd'),
      adults: occupancy.nrAdults,
      children: occupancy.nrChildren,
    };

    let cacheKey = Object.values(params).join('_');
    if (roomId) {
      params.roomId = roomId;
      cacheKey += `_${roomId}`;
    }

    if (addonsCache.value[cacheKey]) {
      return addonsCache.value[cacheKey];
    }

    try {
      isDirty.value = true;
      const results = await http.get('addons', params).then((rsp) => {
        return rsp.data;
      });
      isDirty.value = false;
      addonsCache.value = { ...addonsCache.value, [cacheKey]: results.addons };
      return results.addons;
    } catch (error) {
      console.error('Error fetching addons:', error);
    }
  };

  const clearAddons = () => {
    addons.value = [];
  };

  return {
    isDirty,
    addons,
    addAddon,
    removeAddon,
    getAllAddons,
    clearAddons,
  };
});
